<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import IntegrationItem from './IntegrationItem.vue'
import SettingsLayout from '../SettingsLayout.vue'

// Reactive state for calendar connection
const calendarConnected = ref(false)
const loading = ref(false)

// API helper methods
const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info')
  if (!cookie) {
    console.error('cw_d_session_info cookie não encontrado')
    return {}
  }
  try {
    const sessionInfo = JSON.parse(decodeURIComponent(cookie))
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    }
  } catch (e) {
    console.error('Erro ao analisar cw_d_session_info:', e)
    return {}
  }
}

// Check calendar connection status
const checkCalendarConnection = async () => {
  loading.value = true
  try {
    const headers = getAuthHeaders()
    const response = await axios.get('/api/v1/integrations_responza', {
      headers,
      withCredentials: true,
      params: { type: 'google_calendar' },
    })

    // Check if there's an error from the server
    if (response.data && response.data.error) {
      calendarConnected.value = false
      return
    }

    // Check for the active property in the response
    if (response.data && typeof response.data.active === 'boolean') {
      calendarConnected.value = response.data.active
    } else if (response.data && Array.isArray(response.data.integrations)) {
      // If we got a list of integrations, check if Google Calendar is enabled
      const googleCalendar = response.data.integrations.find(i => i.id === 'google_calendar')
      calendarConnected.value = googleCalendar && googleCalendar.active === true
    } else {
      calendarConnected.value = false
    }
  } catch (err) {
    console.error('Erro na verificação da integração:', err)
    calendarConnected.value = false
  } finally {
    loading.value = false
  }
}

// Lista local de integrações com status dinâmico para Google Calendar
const integrationList = computed(() => [
  {
    id: 'googleCalendar',
    name: 'Google Agenda',
    description: 'Sincronize seus eventos e sua agenda com o Responza, permitindo que você gerencie seus compromissos diretamente na plataforma.',
    enabled: calendarConnected.value,
    logo: 'googlecalendar.png',
    i18n_key: 'googleCalendar',
    action: '/googleCalendar',
    hook_type: 'account',
    allow_multiple_hooks: false,
    settings_json_schema: {
      type: 'object',
      properties: {
        client_id: { type: 'string' },
        client_secret: { type: 'string' }
      },
      required: ['client_id', 'client_secret'],
      additionalProperties: false
    },
    settings_form_schema: [
      { label: 'ID do Cliente', type: 'text', name: 'client_id', validation: 'required' },
      { label: 'Segredo do Cliente', type: 'text', name: 'client_secret', validation: 'required' }
    ],
    visible_properties: ['client_id', 'client_secret'],
    button: '/googleCalendar',
    hooks: []
  }
])

// Check calendar connection on component mount
onMounted(() => {
  checkCalendarConnection()
})
</script>

<template>
  <SettingsLayout :is-loading="false">
    <template #body>
      <!-- Header section matching AppointmentsProducts styling -->
      <div class="flex flex-col items-start w-full pt-4">
        <div class="flex items-center justify-between w-full gap-4">
          <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
            <woot-sidemenu-icon />
            Integrações
          </h1>
        </div>

        <div class="flex flex-col w-full gap-3 text-slate-600 dark:text-slate-300">
          <p class="mb-0 mt-2.5 text-base font-normal line-clamp-5 sm:line-clamp-none max-w-3xl tracking-[-0.1px]">
            Gerencie as integrações com serviços externos para expandir as funcionalidades do Responza. 
            Configure webhooks, conecte calendários e muito mais.
          </p>
        </div>
      </div>

      <!-- Content grid -->
      <div class="flex-grow flex-shrink overflow-auto mt-6">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <IntegrationItem v-for="item in integrationList" :key="item.id" :id="item.id" :logo="item.logo"
            :name="item.name" :description="item.description" :enabled="item.enabled" />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>